/**
 * Compact waveform-peak conversion for chat voice notes.
 *
 * Ported from the Flutter `WaveformPeakHelper` (`membership-flutter-app` PR #687)
 * and aligned with the backend contract (`ghl-revex-backend` PR #11178): the wire
 * form is at most {@link MAX_PEAK_COUNT} integers, each a byte `0..{@link MAX_PEAK_VALUE}`.
 *
 * The UI may render at different widths, so we store one stable audio summary and
 * let each renderer resample it for its own layout. `voiceAmplitudes` (doubles) is
 * the live/local UI signal; `waveformPeaks` (these ints) is the persisted/wire form.
 */

/** Default compact peak count persisted with outgoing voice notes. */
export const DEFAULT_PEAK_COUNT = 64

/** Maximum accepted peak count from stored or remote payloads (backend cap). */
export const MAX_PEAK_COUNT = 80

/** Highest compact peak value used for byte-sized waveform storage. */
export const MAX_PEAK_VALUE = 255

function sanitizeAmplitude(amplitude: number): number {
  if (!Number.isFinite(amplitude)) return 0
  return Math.min(Math.max(amplitude, 0), 1)
}

/**
 * When the quietest bar is this fraction of the loudest (or higher), treat the
 * set as low-contrast (typical mastered music) and stretch min→max so bars
 * aren’t a solid block after max-only scaling.
 */
export const LOW_CONTRAST_MIN_TO_MAX_RATIO = 0.55

/**
 * Peak-relative scaling layered on top of Flutter `AmplitudeNormalizer` output.
 * Loudest sample → 1; low-contrast sets stretch `min..max` → `0..1`.
 */
export function scaleAmplitudesRelativeToPeak(amplitudes: readonly number[]): number[] {
  if (amplitudes.length === 0) return []

  let max = 0
  let min = Number.POSITIVE_INFINITY
  const sanitized = new Array<number>(amplitudes.length)

  for (let i = 0; i < amplitudes.length; i++) {
    const sample = Number.isFinite(amplitudes[i]) ? Math.max(amplitudes[i], 0) : 0
    sanitized[i] = sample
    if (sample > max) max = sample
    if (sample < min) min = sample
  }

  if (max <= 0) return sanitized.map(() => 0)

  const range = max - min
  const lowContrast = range > 0 && min / max >= LOW_CONTRAST_MIN_TO_MAX_RATIO

  if (lowContrast) {
    return sanitized.map(sample => sanitizeAmplitude((sample - min) / range))
  }

  return sanitized.map(sample => sanitizeAmplitude(sample / max))
}

/**
 * Convert shaped samples into compact wire peaks.
 *
 * Pipeline (Flutter + our overlap):
 * 1. Callers shape with Flutter `AmplitudeNormalizer` (RMS → dBFS → normalize).
 * 2. We scale relative to the clip’s max peak (unstick boxy / quiet clips).
 * 3. Flutter `WaveformPeakHelper.build`: max per bucket → byte `0..255`.
 */
export function buildWaveformPeaks(amplitudes: readonly number[], targetCount: number = DEFAULT_PEAK_COUNT): number[] {
  if (targetCount <= 0) return []
  if (amplitudes.length === 0) return []

  const scaled = scaleAmplitudesRelativeToPeak(amplitudes)
  const ratio = scaled.length / targetCount
  const peaks: number[] = new Array(targetCount)

  for (let index = 0; index < targetCount; index++) {
    const start = Math.min(Math.floor(index * ratio), scaled.length - 1)
    const end = Math.min(Math.max(Math.floor((index + 1) * ratio), start + 1), scaled.length)

    let peak = 0
    for (let i = start; i < end; i++) {
      const sample = scaled[i]
      if (sample > peak) peak = sample
    }
    peaks[index] = Math.min(Math.max(Math.round(peak * MAX_PEAK_VALUE), 0), MAX_PEAK_VALUE)
  }

  return peaks
}

/**
 * Decode wire peaks → `0..1` for drawing.
 * Flutter `decode` (`/255`) then our peak-relative scale so older boxy uploads
 * still render with visible shape.
 */
export function decodeWaveformPeaks(waveformPeaks: readonly number[]): number[] {
  const flutterDecoded = waveformPeaks.map(peak => Math.min(Math.max(peak, 0), MAX_PEAK_VALUE) / MAX_PEAK_VALUE)
  return scaleAmplitudesRelativeToPeak(flutterDecoded)
}

/**
 * Resample `0..1` samples to exactly `barCount` bars for rendering at a given
 * width (max per bucket, so loud transients survive). When there are fewer
 * samples than bars, samples are stretched so the waveform fills the width.
 * Empty input yields a flat (zero) bar set so the renderer still draws a baseline.
 */
export function resampleForBars(samples: readonly number[], barCount: number): number[] {
  if (barCount <= 0) return []
  if (samples.length === 0) return new Array(barCount).fill(0)
  if (samples.length === barCount) {
    return samples.map(s => Math.min(Math.max(Number.isFinite(s) ? s : 0, 0), 1))
  }

  const ratio = samples.length / barCount
  return Array.from({ length: barCount }, (_, index) => {
    const start = Math.min(Math.floor(index * ratio), samples.length - 1)
    const end = Math.min(Math.max(Math.floor((index + 1) * ratio), start + 1), samples.length)
    let peak = 0
    for (let i = start; i < end; i++) {
      const sample = sanitizeAmplitude(samples[i])
      if (sample > peak) peak = sample
    }
    return peak
  })
}

/**
 * Live-recording bars: keep only the most recent `barCount` samples and
 * right-align them (pad the leading slots with silence). Matches the comment /
 * post design mock (`recBars.slice(-N)` + `justifyContent: flex-end`) so the
 * waveform scrolls left as new samples arrive instead of stretching history
 * across the full width.
 */
export function resampleLiveBars(samples: readonly number[], barCount: number): number[] {
  if (barCount <= 0) return []
  if (samples.length === 0) return new Array(barCount).fill(0)

  const recent = samples.slice(-barCount).map(sanitizeAmplitude)
  if (recent.length >= barCount) return recent
  return [...new Array(barCount - recent.length).fill(0), ...recent]
}

/** Flutter live-recording edge taper defaults (`_LiveRecordingWaveform`). */
export const LIVE_EDGE_TAPER_BAR_COUNT = 6
export const LIVE_EDGE_TAPER_MIN_SCALE = 0.225
export const LIVE_EDGE_TAPER_MIN_OPACITY = 0.3

/**
 * Ease-out curve so edge bars round into the body instead of a hard chevron
 * (Flutter `_roundedEdgeProgress`).
 */
export function liveEdgeRoundedProgress(progress: number): number {
  const clamped = Math.min(Math.max(progress, 0), 1)
  const remaining = 1 - clamped
  return 1 - remaining * remaining
}

/**
 * Scale + opacity for a live bar by index, tapering toward both edges.
 * Returns `{ scale, opacity }` in `0..1`.
 */
export function liveEdgeTaperForBarIndex(
  index: number,
  barCount: number,
  options: {
    edgeTaperBarCount?: number
    edgeTaperMinScale?: number
    edgeTaperMinOpacity?: number
  } = {}
): { scale: number; opacity: number } {
  const taperCount = Math.max(0, options.edgeTaperBarCount ?? LIVE_EDGE_TAPER_BAR_COUNT)
  const minScale = options.edgeTaperMinScale ?? LIVE_EDGE_TAPER_MIN_SCALE
  const minOpacity = options.edgeTaperMinOpacity ?? LIVE_EDGE_TAPER_MIN_OPACITY
  if (taperCount <= 0 || barCount <= 0) return { scale: 1, opacity: 1 }

  const leftDistance = Math.min(index, taperCount)
  const rightDistance = Math.min(Math.max(barCount - 1 - index, 0), taperCount)
  const edgeDistance = Math.min(leftDistance, rightDistance)
  const edgeProgress = liveEdgeRoundedProgress(edgeDistance / taperCount)
  return {
    scale: minScale + (1 - minScale) * edgeProgress,
    opacity: minOpacity + (1 - minOpacity) * edgeProgress,
  }
}

/**
 * Horizontal scroll offset (px) for one live sample step.
 * Flutter uses `-step * transition` while overflowing.
 */
export function liveScrollOffsetPx(step: number, scrollTransitionProgress: number | null | undefined): number {
  if (scrollTransitionProgress == null || scrollTransitionProgress >= 1) return 0
  const progress = Math.min(Math.max(scrollTransitionProgress, 0), 1)
  if (progress === 0) return 0
  return -step * progress
}

/**
 * Clamp externally supplied peaks to the wire contract and preserve `null` for an
 * unknown waveform. Values are floored to integers, clamped to `[0, MAX_PEAK_VALUE]`,
 * and the array is capped at {@link MAX_PEAK_COUNT}.
 */
export function normalizeWaveformPeaks(waveformPeaks: readonly number[] | null | undefined): number[] | null {
  if (waveformPeaks == null || waveformPeaks.length === 0) return null
  return waveformPeaks.slice(0, MAX_PEAK_COUNT).map(peak => {
    if (!Number.isFinite(peak)) return 0
    return Math.min(Math.max(Math.floor(peak), 0), MAX_PEAK_VALUE)
  })
}
